import { FC, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { FileFormData, GetFilesParams } from '../../../../@paco/entities/File/File'
import {
    addDocument,
    editDocument,
    getDocuments,
    getDocumentsPage,
    removeDocument,
} from '../../../../@paco/redux/files/filesActions'
import useCheckPermission from '../../../../@paco/helpers/hooks/useCheckPermission'
import { getCheckedFormOptionsValue } from '../../../../@paco/helpers/types/formOption'
import { useTypedDispatch, useTypedSelector } from '../../../../@paco/redux/store'

import { DocumentFormData } from '../../../../models'
import { DOCUMENTS, PAGE_SIZES } from '../../../../constants'
import { fetchDepartments } from '../../../../redux/@toolkit/departments/departmentsActions'
import { notifyUsers } from '../../../../redux/app/appActions'
import { setDocumentSearch, setFilter } from '../../../../redux/filter/filterActions'
import { AppFilter } from '../../../../redux/filter-ts/filterModels'
import { fetchDocumentTypes } from '../../../../redux/pz/pzActions'
import { Reducers } from '../../../../redux/reducers'
import SharedDocumentsOverview from '../../containers/SharedDocumentsOverview/SharedDocumentsOverview'

const PAGE_SIZE = PAGE_SIZES.PZ[DOCUMENTS]

const ConnectedSharedDocumentsOverview: FC = () => {
    const dispatch = useDispatch()
    const typedDispatch = useTypedDispatch()

    const filter = useSelector((state: Reducers) => state.filterReducer.filter)
    const pagination = useSelector((state: Reducers) => state.paginationReducer)
    const { roles } = useSelector((state: Reducers) => state.appReducer)
    const { isLoading: isDepartmentsLoading, departments } = useSelector((state: Reducers) => state.departmentsReducer)
    const { departmentOptions } = useTypedSelector(state => state.globalFiltersReducer)

    const { permissions } = useTypedSelector(state => state.authenticatedUserReducer)
    const { documentTypes, lastAddedDocument } = useSelector((state: Reducers) => state.pzReducer)

    // Use new filesReducer for documents
    const {
        isLoading: isFilesLoading,
        documents,
    } = useTypedSelector(state => state.filesReducer)

    const isLoading = isFilesLoading || isDepartmentsLoading
    const canNotifyEmployees = useCheckPermission('notify-all-departments')
    const canAddFiles = useCheckPermission('add-new-files')

    const buildParams = (pageNumber: number): GetFilesParams => {
        const { pz, documentSearch, userTypes } = filter
        const departmentIds = getCheckedFormOptionsValue(departmentOptions)
        const roleIds = userTypes.flatMap(userType => roles.find(role => role.slug === userType)?.id ?? [])

        return {
            search: documentSearch || undefined,
            documentTypes: pz?.documentTypes,
            departments: departmentIds.length > 0 ? departmentIds : undefined,
            start: (pageNumber - 1) * PAGE_SIZE,
            items: PAGE_SIZE,
            fileRoles: roleIds,
        }
    }

    const fetchDocumentsAction = (): void => {
        const pageNumber = pagination.number || 1
        typedDispatch(getDocuments(buildParams(pageNumber)))
    }

    const onPageChange = (pageNumber: number): void => {
        typedDispatch(getDocumentsPage(buildParams(pageNumber)))
    }

    useEffect((): void => {
        if (! documentTypes.length) {
            dispatch(fetchDocumentTypes())
        }
    }, [permissions])

    useEffect((): void => {
        if (! documents.length) {
            fetchDocumentsAction()
        }

        if (! departments.length) {
            typedDispatch(fetchDepartments())
        }
    }, [])

    useEffect((): void => {
        fetchDocumentsAction()
    }, [departmentOptions, filter])

    const onSetFilterChange = (newFilter: AppFilter) => {
        dispatch(setFilter(newFilter))
    }

    const onSearchBarChange = async (search: string) => {
        dispatch(setDocumentSearch(search))
    }

    const onAddDocument = async (formData: DocumentFormData) => {
        const fileFormData: FileFormData = {
            id: formData.id,
            name: formData.name,
            ext: formData.ext,
            base64: formData.base64,
            file: formData.file,
            documentType: formData.documentType,
            roles: formData.roles,
            departments: formData.departments,
        }

        if (formData.id) {
            await typedDispatch(editDocument(formData.id, fileFormData))
        } else {
            await typedDispatch(addDocument(fileFormData))
        }
    }

    const onDeleteDocument = async (documentId: string) => {
        await typedDispatch(removeDocument(documentId))
    }

    const onNotifyUsers = (data: any) => {
        dispatch(notifyUsers(data))
    }

    return (
        <SharedDocumentsOverview
            isLoading={isLoading}
            filter={filter}
            departments={departments}
            roles={roles}
            documentTypes={documentTypes}
            pagination={pagination}
            documents={documents}
            lastAddedDocument={lastAddedDocument}
            onSetFilterChange={onSetFilterChange}
            onSetPagination={onPageChange}
            onSearchBarChange={onSearchBarChange}
            onAddDocument={onAddDocument}
            onDeleteDocument={onDeleteDocument}
            canNotifyEmployees={canNotifyEmployees}
            onNotifyUsers={onNotifyUsers}
            canAddFiles={canAddFiles}
        />
    )
}

export default ConnectedSharedDocumentsOverview
